import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { MdHelpOutline, MdPlayArrow } from "react-icons/md";
import { useLocation } from "react-router-dom";
import { useTour } from "./TourContext";
import { TOURS, GETTING_STARTED_KEY, getCurrentTourKey } from "./tourRegistry";

const POS_STORAGE_KEY = "tour_help_button_pos";
const BTN_SIZE = 56; // matches Chakra IconButton size="lg" round
const DRAG_THRESHOLD = 5; // px of pointer travel before a press counts as a drag

function loadPos() {
  try {
    const raw = localStorage.getItem(POS_STORAGE_KEY);
    if (!raw) return null;
    const { x, y } = JSON.parse(raw);
    if (typeof x === "number" && typeof y === "number") return { x, y };
  } catch (_) {}
  return null;
}

function savePos(pos) {
  try {
    localStorage.setItem(POS_STORAGE_KEY, JSON.stringify(pos));
  } catch (_) {}
}

function clampToViewport(pos) {
  const margin = 8;
  const maxX = Math.max(margin, window.innerWidth - BTN_SIZE - margin);
  const maxY = Math.max(margin, window.innerHeight - BTN_SIZE - margin);
  return {
    x: Math.min(Math.max(margin, pos.x), maxX),
    y: Math.min(Math.max(margin, pos.y), maxY),
  };
}

function defaultPos() {
  return {
    x: window.innerWidth - BTN_SIZE - 24,
    y: window.innerHeight - BTN_SIZE - 24,
  };
}

export default function HelpButton() {
  const { startTour } = useTour();
  const location = useLocation();

  const pageKey = getCurrentTourKey(location.pathname);
  const pageTour = pageKey && TOURS[pageKey];

  const [pos, setPos] = useState(() => clampToViewport(loadPos() || defaultPos()));
  const posRef = useRef(pos);
  useEffect(() => {
    posRef.current = pos;
  }, [pos]);

  // Drag state lives in a ref — we don't need to re-render during the drag
  // beyond what setPos already does.
  const dragRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    baseX: 0,
    baseY: 0,
    moved: false,
  });

  // Keep the button on-screen when the viewport resizes.
  useEffect(() => {
    function onResize() {
      setPos((p) => clampToViewport(p));
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Drag implementation: window-level pointermove/pointerup listeners,
  // attached only while a drag is in progress. We deliberately do NOT use
  // setPointerCapture — capturing the pointer on the wrapper retargets the
  // trailing click event there too, which means PopoverTrigger's onClick on
  // the inner IconButton never fires and the popover won't open on a tap.
  useEffect(() => {
    function onWindowMove(e) {
      const s = dragRef.current;
      if (!s.active || e.pointerId !== s.pointerId) return;
      const dx = e.clientX - s.startX;
      const dy = e.clientY - s.startY;
      if (!s.moved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
      s.moved = true;
      setPos(clampToViewport({ x: s.baseX + dx, y: s.baseY + dy }));
    }
    function onWindowUp(e) {
      const s = dragRef.current;
      if (!s.active || e.pointerId !== s.pointerId) return;
      s.active = false;
      s.pointerId = null;
      if (s.moved) {
        savePos(posRef.current);
      }
      // s.moved stays set so suppressClickIfDragged can intercept the
      // synthetic click that follows; it gets reset there.
    }
    window.addEventListener("pointermove", onWindowMove);
    window.addEventListener("pointerup", onWindowUp);
    window.addEventListener("pointercancel", onWindowUp);
    return () => {
      window.removeEventListener("pointermove", onWindowMove);
      window.removeEventListener("pointerup", onWindowUp);
      window.removeEventListener("pointercancel", onWindowUp);
    };
  }, []);

  function onPointerDown(e) {
    if (e.button !== undefined && e.button !== 0) return; // primary button only
    dragRef.current = {
      active: true,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      baseX: posRef.current.x,
      baseY: posRef.current.y,
      moved: false,
    };
  }

  // Chakra's PopoverTrigger opens the popover via an onClick on its child.
  // After a real drag we don't want that click to register, so intercept
  // it in the capture phase before it reaches PopoverTrigger.
  function suppressClickIfDragged(e) {
    if (dragRef.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      dragRef.current.moved = false;
    }
  }

  return (
    <Box
      data-tour="layout.help"
      position="fixed"
      left={`${pos.x}px`}
      top={`${pos.y}px`}
      zIndex={9000}
      onPointerDown={onPointerDown}
      onClickCapture={suppressClickIfDragged}
      // Disables browser's pan/zoom gestures on touch so the drag works
      // on mobile; cursor is a grab affordance.
      style={{ touchAction: "none", cursor: "grab" }}
    >
      <Popover placement="top-end">
        <Tooltip label="Help & guided tour" placement="left">
          <Box>
            <PopoverTrigger>
              <IconButton
                aria-label="Help"
                icon={<MdHelpOutline size={22} />}
                colorScheme="blue"
                isRound
                size="lg"
                boxShadow="lg"
              />
            </PopoverTrigger>
          </Box>
        </Tooltip>
        <PopoverContent w="260px">
          <PopoverArrow />
          <PopoverHeader fontWeight="semibold">Guided tours</PopoverHeader>
          <PopoverBody>
            <VStack align="stretch" spacing={1}>
              <HelpItem
                label="Replay onboarding tour"
                hint="Quick walk through the whole portal."
                onClick={() => startTour(GETTING_STARTED_KEY)}
              />
              {pageTour && (
                <HelpItem
                  label={`Tour this page (${pageTour.title || pageKey})`}
                  hint="A short overview of this page."
                  onClick={() => startTour(pageKey)}
                />
              )}
              {!pageTour && (
                <Text fontSize="xs" color="gray.400" px={2} py={2}>
                  No mini-tour for this page yet.
                </Text>
              )}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
}

function HelpItem({ label, hint, onClick }) {
  // Chakra Button instead of Box-as-button so the popover's focus
  // manager doesn't intercept the click.
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      justifyContent="flex-start"
      h="auto"
      py={2}
      px={2}
      whiteSpace="normal"
      textAlign="left"
      _hover={{ bg: "blue.50" }}
    >
      <Box color="blue.500" mr={2} mt="2px">
        <MdPlayArrow size={18} />
      </Box>
      <Box>
        <Text fontSize="sm" fontWeight="semibold">{label}</Text>
        <Text fontSize="xs" color="gray.500" fontWeight="normal">{hint}</Text>
      </Box>
    </Button>
  );
}
